import * as THREE from 'three/webgpu';
import {
  pass,
  mrt,
  output,
  emissive,
  uniform,
  time,
  screenUV,
  Fn,
  vec2,
  vec3,
  vec4,
  float,
  abs,
  smoothstep,
  mix,
  If,
  dot,
  fract,
  sin,
  step,
  clamp,
  length,
} from 'three/tsl';
import { bloom } from 'three/addons/tsl/display/BloomNode.js';
import { dof } from 'three/addons/tsl/display/DepthOfFieldNode.js';
import { SkyMesh } from 'three/addons/objects/SkyMesh.js';
import {
  DEFAULT_VISUAL_QUALITY_PRESET,
  type VisualQualityPreset,
} from './visualConfig';
import {
  DEFAULT_WORLD_THEME,
  type WorldThemeDefinition,
} from './worldTheme';
import { hash21 } from './tslUtils';
import { PostFxState, CameraShakeState, type UniformLike } from './postProcessingState';
export { POST_FX_LIMITS } from './postProcessingState';

function applySkySettings(
  sky: SkyMesh,
  sunPosition: THREE.Vector3,
  theme: WorldThemeDefinition,
): void {
  sky.scale.setScalar(450000);
  sky.sunPosition.value.copy(sunPosition);
  sky.turbidity.value = theme.sky.turbidity;
  sky.rayleigh.value = theme.sky.rayleigh;
  sky.mieCoefficient.value = theme.sky.mieCoefficient;
  sky.mieDirectionalG.value = theme.sky.mieDirectionalG;
}

export class SceneManager {
  scene: THREE.Scene;
  camera: THREE.PerspectiveCamera;
  renderer: THREE.WebGPURenderer;

  private readonly qualityPreset: VisualQualityPreset;
  private readonly worldTheme: WorldThemeDefinition;
  private readonly renderPipeline: THREE.RenderPipeline;
  // BloomNode instance — its `threshold`, `strength`, `radius` are UniformNodes.
  // Typed loosely because BloomNode does not re-export a public TS type.
  private readonly bloomPass: {
    threshold: { value: number };
    strength: { value: number };
    radius: { value: number };
  };
  private readonly resizeHandler: () => void;
  private readonly clock = new THREE.Clock();
  private readonly sunPosition: THREE.Vector3;

  // TSL uniforms — updated from triggerDamagePost / triggerCardPost / setVHSEnabled, etc.
  private readonly uVignetteDarkness = uniform(0.03);
  private readonly uChromaticAmount = uniform(0.0002);
  private readonly uGrainIntensity = uniform(0.0018);
  private readonly uEnableVHS = uniform(0.0);
  private readonly uVHSIntensity = uniform(0.35);
  private readonly uRippleCenter = uniform(new THREE.Vector2(0.5, 0.5));
  private readonly uRippleProgress = uniform(1.0);
  private readonly uRippleStrength = uniform(0.0);
  private readonly uSkyTintBottom = uniform(new THREE.Color(0xb7ddff));
  private readonly uSkyTintTop = uniform(new THREE.Color(0x4f9ee3));
  private readonly uDofFocusDistance = uniform(50.0);

  private animationId = 0;
  private rippleActive = false;
  private readonly postFx: PostFxState;
  private readonly shakeState = new CameraShakeState();
  // Scratch vector reused each frame to apply + restore the shake offset
  // around `renderPipeline.render()`. Allocating once keeps GC quiet on
  // long sustained shake windows.
  private readonly _shakeApplied = new THREE.Vector3();
  private readonly _projVec = new THREE.Vector3();
  private readonly sky: SkyMesh;
  private envTarget: THREE.RenderTarget | null = null;
  private pmremGenerator: THREE.PMREMGenerator | null = null;
  private _initPromise: Promise<void> | null = null;

  constructor(
    canvas: HTMLCanvasElement,
    qualityPreset: VisualQualityPreset = DEFAULT_VISUAL_QUALITY_PRESET,
    worldTheme: WorldThemeDefinition = DEFAULT_WORLD_THEME,
  ) {
    this.qualityPreset = qualityPreset;
    this.worldTheme = worldTheme;

    this.renderer = new THREE.WebGPURenderer({
      canvas,
      antialias: true,
      alpha: false,
    });
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, qualityPreset.devicePixelRatioCap));
    this.renderer.setSize(window.innerWidth, window.innerHeight);
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = worldTheme.lighting.exposure;

    this.camera = new THREE.PerspectiveCamera(
      60,
      window.innerWidth / window.innerHeight,
      0.1,
      10000,
    );
    this.camera.position.set(0, 40, 60);

    this.scene = new THREE.Scene();
    this.scene.background = new THREE.Color(worldTheme.sky.horizonColor);
    this.scene.fog = new THREE.FogExp2(worldTheme.sky.fogColor, 0.0009);

    this.sunPosition = new THREE.Vector3();
    const phi = THREE.MathUtils.degToRad(worldTheme.sky.sunPhiDeg);
    const theta = THREE.MathUtils.degToRad(worldTheme.sky.sunThetaDeg);
    this.sunPosition.setFromSphericalCoords(1, phi, theta);

    this.sky = new SkyMesh();
    applySkySettings(this.sky, this.sunPosition, worldTheme);
    this.scene.add(this.sky);

    // PMREMGenerator deferred to init() — compileEquirectangularShader and
    // fromScene require the renderer to be initialized first.

    const ambient = new THREE.AmbientLight(
      worldTheme.lighting.ambientColor,
      worldTheme.lighting.ambientIntensity,
    );
    this.scene.add(ambient);

    const directional = new THREE.DirectionalLight(
      worldTheme.lighting.directionalColor,
      worldTheme.lighting.directionalIntensity,
    );
    directional.position.copy(this.sunPosition).multiplyScalar(180);
    this.scene.add(directional);

    const hemisphere = new THREE.HemisphereLight(
      worldTheme.lighting.hemisphereSky,
      worldTheme.lighting.hemisphereGround,
      worldTheme.lighting.hemisphereIntensity,
    );
    this.scene.add(hemisphere);

    this.uSkyTintBottom.value.set(worldTheme.sky.horizonColor);
    this.uSkyTintTop.value.set(worldTheme.sky.zenithColor);

    const scenePass = pass(this.scene, this.camera);
    scenePass.setMRT(mrt({
      output,
      emissive,
    }));
    const scenePassColor = scenePass.getTextureNode('output');
    const scenePassEmissive = scenePass.getTextureNode('emissive');
    const viewZ = scenePass.getViewZNode();

    // Bloom on the emissive buffer — only non-zero emissive pixels glow.
    const bloomNode = bloom(scenePassEmissive);
    bloomNode.threshold.value = qualityPreset.bloomThreshold;
    bloomNode.strength.value = qualityPreset.bloomStrength;
    bloomNode.radius.value = qualityPreset.bloomRadius;
    this.bloomPass = bloomNode as unknown as {
      threshold: { value: number };
      strength: { value: number };
      radius: { value: number };
    };

    this.postFx = new PostFxState({
      uChromaticAmount: this.uChromaticAmount as unknown as UniformLike,
      uGrainIntensity: this.uGrainIntensity as unknown as UniformLike,
      uVignetteDarkness: this.uVignetteDarkness as unknown as UniformLike,
      uEnableVHS: this.uEnableVHS as unknown as UniformLike,
      bloomStrength: this.bloomPass.strength,
      bloomThreshold: this.bloomPass.threshold,
      basePostIntensity: qualityPreset.postIntensity,
      baseBloomStrength: qualityPreset.bloomStrength,
      baseBloomThreshold: qualityPreset.bloomThreshold,
    });

    // Pipeline: scene (MRT: output+emissive) -> cinematic Fn -> DOF -> +bloom
    const cinematic = Fn(() => {
      const uv = screenUV.toVar();

      // --- Concussive screen ripple (UV warp) ------------------------------
      const rippleVec = uv.sub(this.uRippleCenter);
      const rippleDist = length(rippleVec);
      const rippleFront = this.uRippleProgress.mul(1.5);
      const rippleFactor = smoothstep(float(0.08), float(0.0), abs(rippleDist.sub(rippleFront)))
        .mul(this.uRippleStrength)
        .mul(this.uRippleProgress.oneMinus());
      // Safe normalize: avoid NaN at the exact ripple center by dividing by
      // (dist + epsilon); when dist ≈ 0 the offset magnitude is 0 anyway.
      const rippleDir = rippleVec.div(rippleDist.add(float(0.0001)));
      uv.assign(uv.add(rippleDir.mul(rippleFactor).mul(0.03)));

      // --- VHS jitter + tracking tear --------------------------------------
      If(this.uEnableVHS.greaterThan(0.5), () => {
        const jitterSeed = vec2(time.mul(10.0), uv.y);
        const rawJitter = hash21(jitterSeed);
        If(rawJitter.greaterThan(0.98), () => {
          const jitterShift = hash21(vec2(time, uv.y));
          uv.x.assign(uv.x.add(jitterShift.sub(0.5).mul(0.05).mul(this.uVHSIntensity)));
        });
        uv.x.assign(uv.x.add(sin(uv.y.mul(220.0).add(time.mul(11.0))).mul(0.0008).mul(this.uVHSIntensity)));
        const tracking = step(
          float(0.9),
          fract(time.mul(0.5).add(uv.y.mul(0.5))),
        );
        uv.x.assign(uv.x.add(tracking.mul(0.02).mul(this.uVHSIntensity).mul(sin(time.mul(20.0)))));
      });

      // --- Chromatic aberration (sample scene at offset UVs) ---------------
      const dir = uv.sub(0.5);
      const dist = length(dir);
      const caOffset = dir.mul(dist).mul(this.uChromaticAmount).mul(2.0);
      const r = scenePassColor.sample(uv.add(caOffset)).r;
      const g = scenePassColor.sample(uv).g;
      const b = scenePassColor.sample(uv.sub(caOffset)).b;
      const color = vec4(r, g, b, 1.0).toVar();

      // --- Shadow + mid lift (inlined vec3 smoothstep) --------------------
      const liftT = color.rgb.sub(float(-0.04)).div(float(1.14)).clamp(0.0, 1.0);
      color.rgb.assign(liftT.mul(liftT).mul(vec3(3.0).sub(liftT.mul(2.0))));
      color.rgb.assign(color.rgb.pow(vec3(0.96)));
      const luma = dot(color.rgb, vec3(0.299, 0.587, 0.114));
      const shadowLift = vec3(0.05, 0.07, 0.08);
      color.rgb.addAssign(shadowLift.mul(luma.oneMinus()).mul(0.1));
      color.rgb.mulAssign(float(1.0).add(luma.oneMinus().mul(0.06)));
      color.rgb.addAssign(vec3(0.015, 0.02, 0.026).mul(luma.oneMinus()));

      // --- Sky gradient tint in upper portion ------------------------------
      const skyGradient = mix(
        this.uSkyTintBottom,
        this.uSkyTintTop,
        smoothstep(float(0.34), float(1.0), uv.y),
      );
      const skyMask = smoothstep(float(0.42), float(0.92), uv.y)
        .mul(smoothstep(float(0.58), float(0.98), luma));
      color.rgb.assign(mix(color.rgb, mix(color.rgb, skyGradient, float(0.52)), skyMask));

      // --- Vignette --------------------------------------------------------
      const vignette = clamp(dot(dir, dir).mul(this.uVignetteDarkness).oneMinus(), 0.92, 1.02);
      color.rgb.mulAssign(vignette);

      // --- Procedural film grain ------------------------------------------
      const grainSeed = uv.mul(1024.0).add(fract(time).mul(137.0));
      const grain = hash21(grainSeed)
        .sub(0.5)
        .mul(this.uGrainIntensity)
        .mul(2.0);
      color.rgb.addAssign(grain);

      // --- VHS scanlines ---------------------------------------------------
      If(this.uEnableVHS.greaterThan(0.5), () => {
        const scanline = step(
          float(0.5),
          fract(uv.y.mul(192.0).sub(time.mul(2.0))),
        );
        color.rgb.mulAssign(scanline.mul(0.1).mul(this.uVHSIntensity).oneMinus());
      });

      return color;
    });

    // Chain: scene → cinematic → DOF → +bloom. Feature flags collapse each
    // stage to its input when disabled. Intermediate nodes are cast through
    // `ColorNode` so TS knows `.add` is available (DoF erases its vec4 tag,
    // but `convertToTexture` wraps anything for DOF's input).
    type ColorNode = ReturnType<typeof scenePassColor.add>;
    let finalNode: ColorNode = scenePassColor as unknown as ColorNode;
    if (qualityPreset.features.cinematicPost) {
      finalNode = cinematic() as unknown as ColorNode;
    }
    if (qualityPreset.features.depthOfField) {
      finalNode = dof(
        finalNode,
        viewZ,
        this.uDofFocusDistance,
        float(25.0),
        float(1.0),
      ) as unknown as ColorNode;
    }
    if (qualityPreset.features.selectiveBloom) {
      finalNode = finalNode.add(bloomNode);
    }

    this.renderPipeline = new THREE.RenderPipeline(this.renderer);
    this.renderPipeline.outputNode = finalNode;

    this.resizeHandler = () => this.handleResize();
    window.addEventListener('resize', this.resizeHandler);
  }

  /**
   * Awaits the WebGPU device. Safe to call multiple times — caches the promise.
   * Must complete before the first `renderPipeline.render()`.
   */
  async init(): Promise<void> {
    if (!this._initPromise) {
      this._initPromise = this.renderer.init().then(() => {
        this.pmremGenerator = new THREE.PMREMGenerator(this.renderer);
        this.pmremGenerator.compileEquirectangularShader();
        const envScene = new THREE.Scene();
        const envSky = new SkyMesh();
        applySkySettings(envSky, this.sunPosition, this.worldTheme);
        envScene.add(envSky);
        this.envTarget = this.pmremGenerator.fromScene(envScene);
        this.scene.environment = this.envTarget.texture;
        envSky.geometry.dispose();
        (envSky.material as THREE.Material).dispose();
      });
    }
    await this._initPromise;
  }

  startLoop(onFrame: (deltaMs: number) => void): void {
    const runLoop = async () => {
      await this.init();
      const loop = () => {
        this.animationId = requestAnimationFrame(loop);
        const delta = this.clock.getDelta() * 1000;
        onFrame(delta);

        this.updatePostProcessing(delta / 1000);

        // Apply the screen-shake offset to the camera right before render,
        // then restore it after so downstream readers (next frame's
        // CameraController, raycasts, audio listener) never see the
        // perturbed transform. The shake amount decays inside `update()`.
        this.shakeState.update(delta / 1000);
        const sx = this.shakeState.offset.x;
        const sy = this.shakeState.offset.y;
        const sz = this.shakeState.offset.z;
        const applyShake = sx !== 0 || sy !== 0 || sz !== 0;
        if (applyShake) {
          this._shakeApplied.set(sx, sy, sz);
          this.camera.position.add(this._shakeApplied);
        }

        this.renderPipeline.render();

        if (applyShake) {
          this.camera.position.sub(this._shakeApplied);
        }
      };
      loop();
    };
    void runLoop();
  }

  handleResize(): void {
    const width = window.innerWidth;
    const height = window.innerHeight;
    this.camera.aspect = width / height;
    this.camera.updateProjectionMatrix();
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, this.qualityPreset.devicePixelRatioCap));
    this.renderer.setSize(width, height);
  }

  getScene(): THREE.Scene {
    return this.scene;
  }

  getCamera(): THREE.PerspectiveCamera {
    return this.camera;
  }

  getRenderer(): THREE.WebGPURenderer {
    return this.renderer;
  }

  getSunPosition(): THREE.Vector3 {
    return this.sunPosition;
  }

  setVHSEnabled(enabled: boolean): void {
    this.postFx.queueVHS(enabled);
  }

  triggerDamagePost(intensity: number = 0.35): void {
    this.postFx.triggerDamagePulse(intensity);
  }

  triggerCardPost(intensity: number = 0.24): void {
    this.postFx.triggerCardPulse(intensity);
  }

  /**
   * Trigger a heavy-hit camera kick. The next render-frame samples a fresh
   * shake offset bounded by `±amount`, then decays toward zero at
   * `decayPerSec`. No-op when amount <= 0.
   */
  triggerShake(amount: number = 0.3, decayPerSec: number = 4): void {
    this.shakeState.triggerShake(amount, decayPerSec);
  }

  /** U12 — local swarm HP fraction in [0,1]; drives low-HP visual escalation. */
  setLowHpFraction(fraction: number): void {
    this.postFx.setLowHpFraction(fraction);
  }

  triggerAbilityRipple(worldPos: THREE.Vector3, camera: THREE.PerspectiveCamera): void {
    const ndc = this._projVec.copy(worldPos).project(camera);
    if (ndc.z > 1) return;
    this.uRippleCenter.value.set(ndc.x * 0.5 + 0.5, ndc.y * 0.5 + 0.5);
    this.uRippleProgress.value = 0;
    this.uRippleStrength.value = 0.8;
    this.rippleActive = true;
  }

  setFocusDistance(distance: number): void {
    this.uDofFocusDistance.value = distance;
  }

  dispose(): void {
    cancelAnimationFrame(this.animationId);
    window.removeEventListener('resize', this.resizeHandler);
    this.renderPipeline.dispose?.();
    this.sky.geometry.dispose();
    (this.sky.material as THREE.Material).dispose();
    this.envTarget?.dispose();
    this.pmremGenerator?.dispose();
    this.renderer.dispose();
  }

  private updatePostProcessing(deltaSec: number): void {
    this.postFx.update(deltaSec);

    if (this.rippleActive) {
      const progress = this.uRippleProgress.value + deltaSec * 0.8;
      if (progress >= 1.0) {
        this.uRippleProgress.value = 1.0;
        this.uRippleStrength.value = 0.0;
        this.rippleActive = false;
      } else {
        this.uRippleProgress.value = progress;
      }
    }
  }
}
